import { SpeakingClassLevel, TopicItemFragment } from "../gql/graphql.ts";
import { SortDirection } from "../UI/Table/TableColumnHeader.tsx";

export const TOPIC_TABLE_SORT_FIELDS = [
  "name",
  "level",
  "pdfUrl",
  "userGroups",
] as const;

export type TopicTableSortField = (typeof TOPIC_TABLE_SORT_FIELDS)[number];

export type TopicTableSort = {
  sortBy: TopicTableSortField;
  sortDirection: SortDirection;
};

export const PDF_PRESENCES = ["withPdf", "withoutPdf"] as const;

export type PdfPresence = (typeof PDF_PRESENCES)[number];

export type TopicTableFilters = {
  search: string;
  levels: SpeakingClassLevel[];
  pdfPresences: PdfPresence[];
  userGroupIds: string[];
};

export const SPEAKING_CLASS_LEVELS_FROM_LOWEST: SpeakingClassLevel[] = [
  SpeakingClassLevel.Starters,
  SpeakingClassLevel.Explorers,
  SpeakingClassLevel.Navigators,
  SpeakingClassLevel.Pioneers,
  SpeakingClassLevel.Voyagers,
];

export function speakingClassLevelRank(level: SpeakingClassLevel): number {
  return SPEAKING_CLASS_LEVELS_FROM_LOWEST.indexOf(level);
}

export function userGroupNamesOf(topic: TopicItemFragment): string {
  return topic.userGroups.map((userGroup) => userGroup.name).join(", ");
}

function hasPdf(topic: TopicItemFragment): boolean {
  return topic.pdfUrl.trim().length > 0;
}

function matchesSearch(topic: TopicItemFragment, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  const searchableValues = [
    topic.id,
    topic.name,
    topic.level,
    topic.pdfUrl,
    ...topic.userGroups.map((userGroup) => userGroup.name),
  ];
  return searchableValues.some((value) => value.toLowerCase().includes(query));
}

export function matchesTopicTableFilters(
  topic: TopicItemFragment,
  filters: TopicTableFilters,
): boolean {
  const matchesLevel =
    filters.levels.length === 0 || filters.levels.includes(topic.level);
  const matchesPdfPresence =
    filters.pdfPresences.length === 0 ||
    filters.pdfPresences.includes(hasPdf(topic) ? "withPdf" : "withoutPdf");
  const matchesUserGroup =
    filters.userGroupIds.length === 0 ||
    topic.userGroups.some((userGroup) =>
      filters.userGroupIds.includes(userGroup.id),
    );

  return (
    matchesSearch(topic, filters.search) &&
    matchesLevel &&
    matchesPdfPresence &&
    matchesUserGroup
  );
}
